/**
 * PacienteColgoBanner — aviso al médico cuando el PACIENTE se desconecta del
 * room (colgó o cerró la app) mientras la llamada sigue activa.
 *
 * Detección (dentro del contexto de <LiveKitRoom>):
 *  - Un participante remoto con identity `paciente_…` ESTUVO presente
 *    (ref pacienteEstuvo) y luego desapareció de useRemoteParticipants(),
 *    con la conexión del médico aún Connected.
 *  - Debounce de ~4 s antes de mostrar (evita saltar en micro-reconexiones).
 *  - NO se muestra si el médico colgó / la consulta ya finalizó
 *    (videoFinalizado, estado ≠ en_atencion) ni si el paciente nunca entró.
 *
 * Acciones:
 *  - "Volver a llamar": re-envía el push de llamada
 *    (portal_reintentarLlamadaPaciente — mismo callable y patrón que
 *    PacienteNoContestaBanner), con cooldown de 20 s. Tras invocar muestra
 *    "Llamando de nuevo…" hasta detectar de nuevo la identity paciente_.
 *  - "Seguir esperando": descarta el banner (se re-arma si el paciente
 *    vuelve y cuelga otra vez).
 *  - "Colgar": termina la videollamada con el paciente (primer corte M8) sin
 *    esperar a que reconecte — útil cuando el paciente ya no volverá. Reusa el
 *    mismo handler del dock (onColgar) para mantener un único flujo de cierre.
 * Si el paciente reconecta solo, el banner desaparece automáticamente.
 */
import { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { BellRing, PhoneMissed, PhoneOff } from 'lucide-react';
import { useConnectionState, useRemoteParticipants } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import { functions } from '../../config/firebase';
import { useToast } from '../../contexts/ToastContext';
import type { Consulta } from '../../types';

const reintentarLlamadaFn = httpsCallable<
  { consultaId: string },
  { success: boolean; intento: number; dispositivos: number; aceptados: number }
>(functions, 'portal_reintentarLlamadaPaciente');

const DEBOUNCE_MS = 4000;
const COOLDOWN_SEG = 20;

interface Props {
  consultaId: string;
  consulta: Consulta | null | undefined;
  /** Termina la videollamada con el paciente (primer corte). */
  onColgar?: () => void;
  /** true mientras el corte está en curso (deshabilita el botón). */
  colgando?: boolean;
}

export function PacienteColgoBanner({ consultaId, consulta, onColgar, colgando }: Props) {
  const { addToast } = useToast();
  const participants = useRemoteParticipants();
  const connectionState = useConnectionState();

  const pacientePresente = participants.some(
    (p) => (p.identity ?? '').startsWith('paciente_'),
  );

  const pacienteEstuvoRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const [descartado, setDescartado] = useState(false);
  const [reintentando, setReintentando] = useState(false);
  const [llamandoDeNuevo, setLlamandoDeNuevo] = useState(false);
  const [cooldownHasta, setCooldownHasta] = useState(0);
  const [ahora, setAhora] = useState(() => Date.now());

  // La llamada sigue activa desde la perspectiva de la consulta (el médico NO
  // colgó y la consulta no está finalizada).
  const consultaActiva = !!(
    consulta &&
    consulta.estado === 'en_atencion' &&
    consulta.videoFinalizado !== true
  );

  // Presencia del paciente: marca que estuvo y limpia cualquier estado del
  // banner (volvió solo o tras "Volver a llamar").
  useEffect(() => {
    if (pacientePresente) {
      pacienteEstuvoRef.current = true;
      setVisible(false);
      setDescartado(false);
      setLlamandoDeNuevo(false);
    }
  }, [pacientePresente]);

  // Debounce: el paciente estuvo y desapareció → esperar antes de mostrar.
  useEffect(() => {
    if (pacientePresente || !pacienteEstuvoRef.current) return;
    if (connectionState !== ConnectionState.Connected) return;
    if (!consultaActiva) return;
    const t = setTimeout(() => setVisible(true), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [pacientePresente, connectionState, consultaActiva]);

  // Tick de 1 s solo mientras el banner está visible (cooldown del botón).
  useEffect(() => {
    if (!visible || descartado) return;
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [visible, descartado]);

  if (
    !visible ||
    descartado ||
    pacientePresente ||
    !consultaActiva ||
    connectionState !== ConnectionState.Connected
  ) {
    return null;
  }

  const enCooldown = ahora < cooldownHasta;

  const reintentar = async () => {
    setReintentando(true);
    try {
      const res = await reintentarLlamadaFn({ consultaId });
      setCooldownHasta(Date.now() + COOLDOWN_SEG * 1000);
      setLlamandoDeNuevo(true);
      addToast('success',
        `Llamada reenviada (intento #${res.data.intento}) a ${res.data.dispositivos} dispositivo${res.data.dispositivos === 1 ? '' : 's'} del paciente.`);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'No se pudo reenviar la llamada.');
    } finally {
      setReintentando(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 72,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1150,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: 'var(--color-warning-bg)',
        border: '1px solid var(--color-warning)',
        borderRadius: 'var(--radius-lg)',
        padding: '10px 14px',
        boxShadow: 'var(--shadow-card-hover)',
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      <PhoneMissed
        size={18}
        style={{ color: 'var(--color-warning)', flexShrink: 0 }}
        aria-hidden="true"
      />
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
          El paciente salió de la llamada
        </p>
        <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {llamandoDeNuevo
            ? 'Llamando de nuevo… el banner se cerrará cuando el paciente vuelva a entrar.'
            : 'Se desconectó del room (colgó o cerró la app). Puede volver a llamarlo o seguir esperando a que reconecte.'}
        </p>
      </div>
      <button
        type="button"
        onClick={() => void reintentar()}
        disabled={reintentando || enCooldown}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          border: '1px solid var(--color-warning)', background: 'var(--color-surface)',
          color: 'var(--color-text-primary)', borderRadius: 'var(--radius-md)',
          padding: '6px 12px', fontSize: 'var(--font-size-xs)', fontWeight: 600,
          cursor: reintentando || enCooldown ? 'not-allowed' : 'pointer',
          opacity: reintentando || enCooldown ? 0.6 : 1, whiteSpace: 'nowrap',
        }}
      >
        <BellRing size={13} aria-hidden="true" />
        {reintentando ? 'Enviando…' : enCooldown ? `Espere ${Math.ceil((cooldownHasta - ahora) / 1000)}s` : 'Volver a llamar'}
      </button>
      <button
        type="button"
        onClick={() => setDescartado(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          border: '1px solid var(--color-border)', background: 'transparent',
          color: 'var(--color-text-secondary)', borderRadius: 'var(--radius-md)',
          padding: '6px 12px', fontSize: 'var(--font-size-xs)', fontWeight: 600,
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        Seguir esperando
      </button>
      {/* Colgar: si el paciente no volverá, el médico termina la llamada aquí
          mismo (no tiene que buscar el dock). Reusa el handler del corte M8. */}
      {onColgar && (
        <button
          type="button"
          onClick={() => onColgar()}
          disabled={colgando}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            border: 'none', background: '#c0392b', color: '#fff',
            borderRadius: 'var(--radius-md)',
            padding: '6px 12px', fontSize: 'var(--font-size-xs)', fontWeight: 600,
            cursor: colgando ? 'not-allowed' : 'pointer',
            opacity: colgando ? 0.6 : 1, whiteSpace: 'nowrap',
          }}
        >
          <PhoneOff size={13} aria-hidden="true" />
          {colgando ? 'Terminando…' : 'Colgar'}
        </button>
      )}
    </div>
  );
}
